import { Feature, Map, Overlay } from "ol";
import Polygon from 'ol/geom/Polygon';
import VectorLayer from "ol/layer/Vector";
import { Stroke, Fill, Style } from "ol/style";
import VectorSource from "ol/source/Vector";
import { TooltipManager } from "./tooltip";
import { geojson } from "@lib/geojson";
import { FeatureTooltip } from "./marker";

export interface PolygonType {
    coordinates: geojson.Coord[][],
    content: string,
    color: string,
}

export class PolygonManager {
    private readonly mapElement: Map;
    private readonly tooltip: TooltipManager;
    public polygonFeatures = new Set<FeatureTooltip<Polygon>>();
    private vectorSource = new VectorSource();
    private vectorLayer: VectorLayer;

    constructor(mapElement: Map, tooltip: TooltipManager) {
        this.mapElement = mapElement;
        this.tooltip = tooltip;
        this.vectorLayer = new VectorLayer({ source: this.vectorSource });
        this.mapElement.addLayer(this.vectorLayer);
    }

    add(item: PolygonType) {
        const polygonFeature = new Feature(new Polygon(item.coordinates));
        polygonFeature.set('name', item.content);
        
        this.renderPolygon(polygonFeature, item.color);
        this.addHoverEvents(polygonFeature, item);
        this.polygonFeatures.add({
            feature: polygonFeature,
            content: item.content,
            coordinate: item.coordinates[0][0],
            category: 'group',
            type: 'muckpile'
        });
    }

    public clearAll() {
        this.vectorSource.clear();
        this.tooltip.hideAllTooltips('muckpile');
        this.polygonFeatures.clear();
    }

    private renderPolygon(polygonFeature: Feature<Polygon>, color: string) {
        let style = new Style({
            fill: new Fill({ color: hexToRGBA(color, 0.7) }),
            stroke: new Stroke({ color: hexToRGBA(color, 1), width: 1.5 }),
        });

        polygonFeature.setStyle(style);
        this.vectorSource.addFeature(polygonFeature);
    }

    private addHoverEvents(polygonFeature: Feature<Polygon>, item: PolygonType) {
        this.mapElement.on('pointermove', (event) => {
            const pixel = this.mapElement.getEventPixel(event.originalEvent);
            const featureAtPixel = this.mapElement.forEachFeatureAtPixel(pixel, (feature) => feature);

            if (featureAtPixel === polygonFeature) {
                this.tooltip.showTooltip([{ feature: polygonFeature, content: item.content, coordinate: item.coordinates[0][0], category: 'individual', type: 'muckpile' }]);
            } else {
                this.tooltip.hideTooltip(polygonFeature);
            }
        });
    }
}

function hexToRGBA(hex: string, opacity: number): string {
    const trimmedHex = hex.replace('#', '');
    const r = parseInt(trimmedHex.substring(0, 2), 16);
    const g = parseInt(trimmedHex.substring(2, 4), 16);
    const b = parseInt(trimmedHex.substring(4, 6), 16);
    return `rgba(${r}, ${g}, ${b}, ${opacity})`;
}